// Single source of truth for every color used on the /intel map surface.
// Adding a color elsewhere is a review violation — extend the palette.
//
// Color decisions are layered (see LayerSpec.colorAxis):
//   - affiliation (MIL-STD-2525) → frame fill for entity icons
//   - severity                   → tint for signals (sanctions, anomalies)
//   - recency                    → opacity (recent = full, old = faded)
//   - category                   → layer-level color (e.g. supply = magenta)
//
// Theme reactivity:
//   - Tokens come from CSS custom properties on `:root` (e.g. --mil-friendly).
//   - When the host app flips a theme (data-theme attribute, class on <html>,
//     or inline style override), `subscribePaletteChange` listeners fire and
//     the cached snapshot is invalidated. Next `getMapPalette()` returns a
//     fresh tuple set, and React subscribers (`useMapPalette`) re-render.
//   - SSR-safe: when `document` is undefined, `getMapPalette()` returns the
//     literal-fallback palette and `subscribePaletteChange` is a no-op.

export type RGBA = [number, number, number, number];

export type Affiliation = 'friendly' | 'hostile' | 'neutral' | 'unknown';

export interface MapPalette {
  // affiliation (MIL-STD-2525)
  friendly: RGBA;
  hostile: RGBA;
  unknown: RGBA;
  neutral: RGBA;

  // severity heat ramp (green → orange → red).
  //   ok       0-39   chill
  //   watch    40-69  worth a glance
  //   elevated 70-89  active interest
  //   hot      ≥90    drop-everything
  sevOk: RGBA;
  sevWatch: RGBA;
  sevElevated: RGBA;
  sevHot: RGBA;

  // ui
  aoi: RGBA;
  aoiFill: RGBA;

  heatmapStops: {
    transparent: RGBA;
    low: RGBA;
    info: RGBA;
    warn: RGBA;
    high: RGBA;
    hot: RGBA;
  };

  warZoneFill: {
    extreme: RGBA;
    high: RGBA;
    elevated: RGBA;
    low: RGBA;
  };
  warZoneLine: {
    extreme: RGBA;
    high: RGBA;
    elevated: RGBA;
    low: RGBA;
  };

  densityStroke: RGBA;
}

// ─────────────────────────────────────────────────────────────────────────────
// Token cache + reader
// ─────────────────────────────────────────────────────────────────────────────

let cssRoot: CSSStyleDeclaration | null = null;
function rootStyle(): CSSStyleDeclaration | null {
  if (cssRoot) return cssRoot;
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (!document.documentElement) return null;
  cssRoot = window.getComputedStyle(document.documentElement);
  return cssRoot;
}

function parseHex(hex: string): [number, number, number] | null {
  const h = hex.replace('#', '').trim();
  if (h.length === 3) {
    const r = Number.parseInt(`${h.charAt(0)}${h.charAt(0)}`, 16);
    const g = Number.parseInt(`${h.charAt(1)}${h.charAt(1)}`, 16);
    const b = Number.parseInt(`${h.charAt(2)}${h.charAt(2)}`, 16);
    if ([r, g, b].some(Number.isNaN)) return null;
    return [r, g, b];
  }
  if (h.length === 6) {
    const r = Number.parseInt(h.slice(0, 2), 16);
    const g = Number.parseInt(h.slice(2, 4), 16);
    const b = Number.parseInt(h.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return null;
    return [r, g, b];
  }
  return null;
}

/** Convert HSL → RGB (each channel in [0, 255]). Accepts h in [0, 360),
 *  s/l as 0..1 (fractional). Pure function; no DOM. */
function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hPrime = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hPrime % 2) - 1));
  let r1 = 0;
  let g1 = 0;
  let b1 = 0;
  if (hPrime >= 0 && hPrime < 1) [r1, g1, b1] = [c, x, 0];
  else if (hPrime < 2) [r1, g1, b1] = [x, c, 0];
  else if (hPrime < 3) [r1, g1, b1] = [0, c, x];
  else if (hPrime < 4) [r1, g1, b1] = [0, x, c];
  else if (hPrime < 5) [r1, g1, b1] = [x, 0, c];
  else [r1, g1, b1] = [c, 0, x];
  const m = l - c / 2;
  return [Math.round((r1 + m) * 255), Math.round((g1 + m) * 255), Math.round((b1 + m) * 255)];
}

/** Parse `hsl(h s% l%)` / `hsl(h, s%, l%)` / `hsla(h s% l% / a)`. Returns
 *  null on any malformed input. Tolerant of comma- and space-separated
 *  forms because both are valid CSS. Alpha defaults to null so the caller
 *  can decide whether to inherit from the fallback. */
function parseHsl(input: string): [number, number, number, number | null] | null {
  const trimmed = input.trim();
  // Match function call: hsl(...) or hsla(...).
  const m = /^hsla?\(([^)]+)\)$/i.exec(trimmed);
  if (!m) return null;
  // Split on either `/` (alpha separator) or commas; then collapse whitespace.
  const body = m[1];
  if (!body) return null;
  const parts = body.split('/').map((s) => s.trim());
  const main = parts[0]?.replace(/,/g, ' ').split(/\s+/).filter(Boolean) ?? [];
  if (main.length < 3) return null;
  const h = Number.parseFloat(main[0] ?? '');
  const sRaw = main[1] ?? '';
  const lRaw = main[2] ?? '';
  if (!sRaw.endsWith('%') || !lRaw.endsWith('%')) return null;
  const s = Number.parseFloat(sRaw) / 100;
  const l = Number.parseFloat(lRaw) / 100;
  if (Number.isNaN(h) || Number.isNaN(s) || Number.isNaN(l)) return null;
  let alpha: number | null = null;
  // Alpha lives after `/` in space-syntax, or as the 4th comma arg.
  const alphaRaw = parts[1] ?? (main.length >= 4 ? main[3] : undefined);
  if (alphaRaw !== undefined) {
    const a = alphaRaw.endsWith('%')
      ? Number.parseFloat(alphaRaw) / 100
      : Number.parseFloat(alphaRaw);
    if (!Number.isNaN(a)) alpha = Math.max(0, Math.min(1, a));
  }
  const [r, g, b] = hslToRgb(h, s, l);
  return [r, g, b, alpha];
}

const tokenCache = new Map<string, RGBA>();

function readToken(name: string, fallback: RGBA): RGBA {
  const cached = tokenCache.get(name);
  if (cached) return cached;
  const root = rootStyle();
  if (!root) {
    tokenCache.set(name, fallback);
    return fallback;
  }
  const raw = root.getPropertyValue(name).trim();
  // Try hex first (existing dark palette uses #rrggbb everywhere); fall back
  // to hsl/hsla for the heat ramp tokens introduced in W1-A.
  const hex = parseHex(raw);
  if (hex) {
    const out: RGBA = [hex[0], hex[1], hex[2], fallback[3]];
    tokenCache.set(name, out);
    return out;
  }
  const hsl = parseHsl(raw);
  if (hsl) {
    const [r, g, b, a] = hsl;
    const alpha = a === null ? fallback[3] : Math.round(a * 255);
    const out: RGBA = [r, g, b, alpha];
    tokenCache.set(name, out);
    return out;
  }
  tokenCache.set(name, fallback);
  return fallback;
}

/** Invalidate the cached `getComputedStyle` reference + token tuples.
 *  Called automatically when the MutationObserver detects a theme change;
 *  exposed for tests that mutate `:root` and want a clean read. */
export function _resetPaletteCache(): void {
  tokenCache.clear();
  cssRoot = null;
  paletteSnapshot = null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Snapshot builder
// ─────────────────────────────────────────────────────────────────────────────

function buildPalette(): MapPalette {
  return {
    friendly: readToken('--mil-friendly', [52, 120, 246, 230]),
    hostile: readToken('--mil-hostile', [217, 48, 32, 230]),
    unknown: readToken('--mil-unknown', [240, 192, 32, 230]),
    neutral: readToken('--mil-neutral', [122, 122, 122, 230]),

    // Heat ramp — green → orange → red. Hex fallbacks mirror the HSL stops in
    // tokens.css so SSR / no-DOM consumers paint the right hues without DOM.
    sevOk: readToken('--sev-ok', [45, 204, 112, 230]), // hsl(140 65% 50%)
    sevWatch: readToken('--sev-watch', [255, 163, 26, 230]), // hsl(38 100% 55%)
    sevElevated: readToken('--sev-elevated', [250, 107, 28, 230]), // hsl(20 95% 55%)
    sevHot: readToken('--sev-hot', [232, 61, 61, 230]), // hsl(0 80% 58%)

    aoi: readToken('--cyan', [46, 184, 199, 220]),
    aoiFill: [46, 184, 199, 24],

    // Heatmap kernel ramp — 6 stops, low → high. Walks the same hue path as
    // the severity ramp (green → yellow-green → amber → orange → red) so the
    // density read composes with the per-marker severity read.
    heatmapStops: {
      transparent: readToken('--heat-ramp-0', [45, 204, 112, 0]),
      low: readToken('--heat-ramp-1', [45, 204, 112, 89]),
      info: readToken('--heat-ramp-2', [180, 220, 60, 140]),
      warn: readToken('--heat-ramp-3', [255, 163, 26, 179]),
      high: readToken('--heat-ramp-4', [250, 107, 28, 217]),
      hot: readToken('--heat-ramp-5', [232, 61, 61, 242]),
    },

    // war zones (curated polygons) — 4-stop severity ramp with alpha baked
    // in. Same red→yellow as the severity ramp but with translucent fills so
    // the basemap reads through.
    warZoneFill: {
      extreme: [255, 71, 87, 90], // ≥90
      high: [255, 107, 71, 70], // 75-89
      elevated: [255, 165, 2, 55], // 60-74
      low: [255, 215, 0, 40], // <60
    },
    warZoneLine: {
      extreme: [255, 100, 110, 230],
      high: [255, 130, 100, 200],
      elevated: [255, 180, 60, 180],
      low: [255, 220, 90, 160],
    },

    densityStroke: [10, 10, 10, 160],
  };
}

let paletteSnapshot: MapPalette | null = null;

/**
 * Return the current palette snapshot. Cheap when the cache is warm — a
 * single object reference. After a theme change (or `_resetPaletteCache`),
 * the next call rebuilds and returns a *new* object, which lets React's
 * `useSyncExternalStore` detect the change by identity.
 *
 * Safe to call from deck.gl layer builders (per-frame). Avoid calling from
 * per-row callbacks (`getColor` for thousands of rows): capture the snapshot
 * once at the top of the builder and read from the local variable.
 */
export function getMapPalette(): MapPalette {
  if (paletteSnapshot) return paletteSnapshot;
  paletteSnapshot = buildPalette();
  return paletteSnapshot;
}

// ─────────────────────────────────────────────────────────────────────────────
// Subscribe — MutationObserver on <html> for theme changes
// ─────────────────────────────────────────────────────────────────────────────

type Listener = () => void;
const listeners = new Set<Listener>();
let observer: MutationObserver | null = null;

function ensureObserver(): void {
  if (observer) return;
  if (typeof document === 'undefined' || !document.documentElement) return;
  if (typeof MutationObserver === 'undefined') return;
  observer = new MutationObserver(() => {
    _resetPaletteCache();
    for (const fn of listeners) fn();
  });
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style', 'class', 'data-theme'],
  });
}

function teardownObserverIfIdle(): void {
  if (!observer) return;
  if (listeners.size > 0) return;
  observer.disconnect();
  observer = null;
}

/**
 * Register a listener invoked whenever a CSS custom property on `:root` may
 * have changed (style/class/data-theme attribute mutations). Returns an
 * unsubscribe. SSR-safe: when there is no `document`, registers nothing and
 * returns a no-op.
 */
const NOOP = (): void => {
  /* SSR fallback: no DOM, nothing to subscribe to. */
};

export function subscribePaletteChange(listener: Listener): () => void {
  if (typeof document === 'undefined') return NOOP;
  listeners.add(listener);
  ensureObserver();
  return () => {
    listeners.delete(listener);
    teardownObserverIfIdle();
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Derived helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Severity → ramp bucket, against the live snapshot. Stable thresholds
 *  match the heat-scale legend bands:
 *    HOT      ≥90
 *    ELEVATED 70-89
 *    WATCH    40-69
 *    OK       <40
 *  Layer code that wants the same boundaries should call this; do not
 *  re-inline the thresholds. */
export function severityRamp(score: number): RGBA {
  const p = getMapPalette();
  if (score >= 90) return p.sevHot;
  if (score >= 70) return p.sevElevated;
  if (score >= 40) return p.sevWatch;
  return p.sevOk;
}

/** Severity ramp as a single weight in [0, 1] for the heatmap kernel. */
export function severityWeight(severity: number): number {
  return Math.max(0, Math.min(1, severity / 100));
}

const HOUR_MS = 3_600_000;

export function recencyOpacity(ageMs: number): number {
  if (ageMs <= HOUR_MS) return 1.0;
  if (ageMs <= 6 * HOUR_MS) return 0.7;
  if (ageMs <= 24 * HOUR_MS) return 0.4;
  return 0.2;
}

/** Apply a multiplicative alpha to an RGBA, clamped to [0, 255]. */
export function withAlpha(rgba: RGBA, alpha: number): RGBA {
  const a = Math.max(0, Math.min(255, Math.round(rgba[3] * alpha)));
  return [rgba[0], rgba[1], rgba[2], a];
}

// ─────────────────────────────────────────────────────────────────────────────
// Legend metadata — colors live on the snapshot, so consumers that want
// theme-reactive swatches must read them through `getMapPalette()` (or the
// hook) rather than capturing `SEVERITY_BANDS`/`AFFILIATIONS` once.
// ─────────────────────────────────────────────────────────────────────────────

export interface SeverityBand {
  label: 'OK' | 'WATCH' | 'ELEVATED' | 'HOT';
  range: string;
  color: RGBA;
}

export interface AffiliationBand {
  id: Affiliation;
  label: string;
  shape: 'diamond' | 'square' | 'clover' | 'oval';
  color: RGBA;
}

export interface RecencyBand {
  label: '≤1h' | '≤6h' | '≤24h' | 'older';
  opacity: number;
}

export function getSeverityBands(palette: MapPalette = getMapPalette()): readonly SeverityBand[] {
  return [
    { label: 'OK', range: '<40', color: palette.sevOk },
    { label: 'WATCH', range: '40–69', color: palette.sevWatch },
    { label: 'ELEVATED', range: '70–89', color: palette.sevElevated },
    { label: 'HOT', range: '≥90', color: palette.sevHot },
  ];
}

export function getAffiliationBands(
  palette: MapPalette = getMapPalette(),
): readonly AffiliationBand[] {
  return [
    { id: 'friendly', label: 'Friendly', shape: 'diamond', color: palette.friendly },
    { id: 'hostile', label: 'Hostile', shape: 'square', color: palette.hostile },
    { id: 'unknown', label: 'Unknown', shape: 'clover', color: palette.unknown },
    { id: 'neutral', label: 'Neutral', shape: 'oval', color: palette.neutral },
  ];
}

export const RECENCY_BANDS: readonly RecencyBand[] = [
  { label: '≤1h', opacity: 1.0 },
  { label: '≤6h', opacity: 0.7 },
  { label: '≤24h', opacity: 0.4 },
  { label: 'older', opacity: 0.2 },
];

/** Turn an RGBA into a CSS color string for legend swatches. */
export function rgbaToCss(rgba: RGBA): string {
  const [r, g, b, a] = rgba;
  return `rgba(${r}, ${g}, ${b}, ${(a / 255).toFixed(3)})`;
}

/**
 * CSS custom-property name (with the `var(...)` wrapper) for a given
 * SignalType. Used by HTML callouts (EntityPopup, AlertTape) that style
 * with `var(--token)` rather than RGBA tuples — keeps the categorical
 * mapping in lockstep with the palette.
 */
export function signalTypeCssToken(signalType: string): string {
  switch (signalType) {
    case 'SAN':
      return 'var(--red)';
    case 'CFL':
      return 'var(--amber)';
    case 'SUP':
      return 'var(--magenta)';
    case 'FIR':
      return 'var(--amber)';
    case 'SEI':
      return 'var(--cyan)';
    case 'NEW':
      return 'var(--blue)';
    case 'WEA':
      return 'var(--cyan)';
    case 'OBL':
      return 'var(--primary)';
    case 'POL':
      return 'var(--cyan)';
    case 'ANO':
      return 'var(--magenta)';
    default:
      return 'var(--fg-2)';
  }
}
